import { UNASSIGNED_FILTER_VALUE } from 'components/tasks/utils';
import { Project } from 'services/projects/types';
import { isDueDatePast } from 'utils/date';
import { applyRangeSelection, getItemsInRange } from 'utils/rangeSelection';

export type ProjectStatus = NonNullable<Project['status']>;

const PROJECT_STATUSES: ProjectStatus[] = ['Blocked', 'Cancelled', 'Completed', 'Doing', 'Todo'];

export const projectMatchesAssignees = (
  project: Pick<Project, 'assignee'>,
  assignees?: string[],
) => {
  if (!assignees?.length) {
    return true;
  }
  const includeUnassigned = assignees.includes(UNASSIGNED_FILTER_VALUE);
  const ids = assignees.filter(id => id !== UNASSIGNED_FILTER_VALUE);
  if (includeUnassigned && !project.assignee) {
    return true;
  }
  return !!project.assignee && ids.includes(project.assignee);
};

export const projectStatus = (project?: Pick<Project, 'status'> | null): ProjectStatus => {
  if (project?.status && PROJECT_STATUSES.includes(project.status)) {
    return project.status;
  }
  return 'Todo';
};

const getLateProjects = (projects, _tasks) => {
  return projects
    .filter(project => {
      const status = projectStatus(project);
      return !project.isArchived
        && status !== 'Cancelled'
        && status !== 'Completed'
        && isDueDatePast(project.dueAt);
    });
};

export interface NestedProject {
  depth: number;
  parent?: Project;
  previousSibling?: Project;
  project: Project;
}

export const MAX_PROJECT_DEPTH = 2;

export const childIdsByParent = (
  projects: Pick<Project, 'id' | 'parentProject'>[],
): Map<string, string[]> => {
  const children = new Map<string, string[]>();
  for (const project of projects) {
    if (!project.id || !project.parentProject) {
      continue;
    }
    const childIds = children.get(project.parentProject) ?? [];
    childIds.push(project.id);
    children.set(project.parentProject, childIds);
  }
  return children;
};

export const projectDepth = (
  project: Pick<Project, 'id' | 'parentProject'>,
  byId: ReadonlyMap<string, Pick<Project, 'id' | 'parentProject'>>,
): number => {
  let depth = 0;
  let current = project;
  const seen = new Set<string>();
  while (current.parentProject) {
    if (current.id) {
      if (seen.has(current.id)) {
        return depth;
      }
      seen.add(current.id);
    }
    const parent = byId.get(current.parentProject);
    if (!parent) {
      return depth;
    }
    depth += 1;
    current = parent;
  }
  return depth;
};

export const projectDescendantDepth = (
  projectId: string | undefined,
  childrenByParent: ReadonlyMap<string, string[]>,
): number => {
  if (!projectId) {
    return 0;
  }
  const walk = (id: string, seen: Set<string>): number => {
    if (seen.has(id)) {
      return 0;
    }
    const children = childrenByParent.get(id) ?? [];
    if (!children.length) {
      return 0;
    }
    const nextSeen = new Set(seen);
    nextSeen.add(id);
    return 1 + Math.max(...children.map(childId => walk(childId, nextSeen)));
  };
  return walk(projectId, new Set());
};

export const canChooseParentProject = (
  project: Pick<Project, 'id'> | null | undefined,
  projects: Pick<Project, 'id' | 'parentProject'>[] = [],
): boolean => {
  if (!project?.id) {
    return true;
  }
  return projectDescendantDepth(project.id, childIdsByParent(projects)) < MAX_PROJECT_DEPTH;
};

export const eligibleParentProjects = <T extends Pick<Project, 'id' | 'parentProject'>>(
  projects: T[],
  project?: Pick<Project, 'id'> | null,
): T[] => {
  const byId = new Map(
    projects
      .filter((item): item is T & { id: string } => !!item.id)
      .map(item => [item.id, item]),
  );
  const children = childIdsByParent(projects);
  const extra = project?.id ? projectDescendantDepth(project.id, children) : 0;
  const descendants = new Set<string>();
  if (project?.id) {
    const stack = [...(children.get(project.id) ?? [])];
    while (stack.length) {
      const id = stack.pop();
      if (!id || descendants.has(id)) {
        continue;
      }
      descendants.add(id);
      stack.push(...(children.get(id) ?? []));
    }
  }
  return projects.filter(item => {
    if (!item.id || item.id === project?.id || descendants.has(item.id)) {
      return false;
    }
    return projectDepth(item, byId) + 1 + extra <= MAX_PROJECT_DEPTH;
  });
};

const sortableProjectName = (name: string) =>
  name.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();

export const sortProjectsByName = <T extends Pick<Project, 'id' | 'name'>>(
  projects: T[],
  nameOverrides: Readonly<Record<string, string>> = {},
): T[] => [...projects].sort((left, right) => {
  const leftName = left.id in nameOverrides ? nameOverrides[left.id] : left.name;
  const rightName = right.id in nameOverrides ? nameOverrides[right.id] : right.name;
  const leftKey = sortableProjectName(leftName || '');
  const rightKey = sortableProjectName(rightName || '');
  if (leftKey < rightKey) {
    return -1;
  }
  if (leftKey > rightKey) {
    return 1;
  }
  return left.id.localeCompare(right.id);
});

export const nestProjects = (projects: Project[] = []): NestedProject[] => {
  const byId = new Map(
    projects
      .filter((project): project is Project & { id: string } => !!project.id)
      .map(project => [project.id, project]),
  );
  const childrenByParent = new Map<string, Project[]>();
  const roots: Project[] = [];

  for (const project of projects) {
    const parentId = project.parentProject;
    const parent = parentId ? byId.get(parentId) : undefined;
    if (parent?.id && projectDepth(parent, byId) < MAX_PROJECT_DEPTH) {
      const children = childrenByParent.get(parent.id) ?? [];
      children.push(project);
      childrenByParent.set(parent.id, children);
    } else {
      roots.push(project);
    }
  }

  const result: NestedProject[] = [];
  const visiting = new Set<string>();

  const walk = (project: Project, depth: number, parent?: Project, previousSibling?: Project) => {
    if (project.id && visiting.has(project.id)) {
      return;
    }
    if (project.id) {
      visiting.add(project.id);
    }
    result.push({
      depth,
      parent,
      previousSibling,
      project,
    });
    if (project.id) {
      const children = childrenByParent.get(project.id) ?? [];
      let previousChild: Project | undefined;
      for (const child of children) {
        walk(child, depth + 1, project, previousChild);
        previousChild = child;
      }
      visiting.delete(project.id);
    }
  };

  let previousRoot: Project | undefined;
  for (const root of roots) {
    walk(root, 0, undefined, previousRoot);
    previousRoot = root;
  }

  return result;
};

const getProjectsInRange = (projects: Project[], fromId: string, toId: string): Project[] => (
  getItemsInRange(projects, fromId, toId)
);

interface ApplyProjectSelectionParams {
  displayedProjects: Project[];
  lastSelectedProjectId?: string;
  project: Project;
  selectedProjects: Project[];
  shiftKey: boolean;
}

const applyProjectSelection = ({
  displayedProjects,
  lastSelectedProjectId,
  project,
  selectedProjects,
  shiftKey,
}: ApplyProjectSelectionParams): {
  lastSelectedProjectId?: string;
  selectedProjects: Project[];
} => {
  const result = applyRangeSelection({
    displayedItems: displayedProjects,
    item: project,
    lastSelectedId: lastSelectedProjectId,
    selectedItems: selectedProjects,
    shiftKey,
  });
  return {
    lastSelectedProjectId: result.lastSelectedId,
    selectedProjects: result.selectedItems,
  };
};

export {
    applyProjectSelection,
    getLateProjects,
    getProjectsInRange
};
